import React, { useState, useEffect } from 'react';
import weighingService from '@/services/weighing';
import {
  Camion,
  Chauffeur,
  Produit,
  Fournisseur,
  PontBascule,
  PeséeCreate
} from '@/types';

interface WeighingFormProps {
  onSubmit: (data: PeséeCreate) => void;
  onCancel: () => void;
}

const WeighingForm: React.FC<WeighingFormProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<PeséeCreate>({
    reference: '',
    poids_brut: 0,
    observations: '',
    camion: 0,
    chauffeur: 0,
    produit: 0,
    fournisseur: 0,
    pont_bascule: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Données pour les listes déroulantes
  const [camions, setCamions] = useState<Camion[]>([]);
  const [chauffeurs, setChauffeurs] = useState<Chauffeur[]>([]);
  const [produits, setProduits] = useState<Produit[]>([]);
  const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
  const pontsBascule: PontBascule[] = [];


  // Charger les données initiales
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // Charger les camions
        const camionsResponse = await weighingService.getCamions();
        if (camionsResponse && camionsResponse.results) {
          setCamions(camionsResponse.results || []);
        }

        // Charger les chauffeurs
        const chauffeursResponse = await weighingService.getChauffeurs();
        if (chauffeursResponse && chauffeursResponse.results) {
          setChauffeurs(chauffeursResponse.results || []);
        }

        // Charger les produits
        const produitsResponse = await weighingService.getProduits();
        if (produitsResponse && produitsResponse.results) {
          setProduits(produitsResponse.results || []);
        }

        // Charger les fournisseurs
        const fournisseursResponse = await weighingService.getFournisseurs();
        if (fournisseursResponse && fournisseursResponse.results) {
          setFournisseurs(fournisseursResponse.results || []);
        }

        // Charger les ponts bascules - méthode non disponible dans WeighingService
        // setPontsBascule([]);

      } catch (err: any) {
        setError('Erreur lors du chargement des données: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('poids') ? parseFloat(value) || 0 : parseInt(value) || 0
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.reference || !formData.poids_brut) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Créer la nouvelle pesée
      const response = await weighingService.nouvellePesée(formData);
      
      if (response.success) {
        onSubmit(formData);
      } else {
        setError(response.message || 'Erreur lors de la création de la pesée');
      }
    } catch (err: any) {
      setError('Erreur lors de la création de la pesée: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !camions.length) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Chargement des données...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Nouvelle Pesée - Pont Bascule
        </h2>
        <p className="text-gray-600">Formulaire de création d'une nouvelle pesée</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* En-tête avec référence et produit */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              N° Référence *
            </label>
            <input
              type="text"
              name="reference"
              value={formData.reference}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Référence unique"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Produit
            </label>
            <select
              name="produit"
              value={formData.produit}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>Sélectionner un produit</option>
              {produits.map(prod => (
                <option key={prod.id} value={prod.id}>
                  {prod.nom} ({prod.type_display})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Poids Brut (kg) *
            </label>
            <input
              type="number"
              name="poids_brut"
              value={formData.poids_brut}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
              required
            />
          </div>
        </div>

        {/* Section Transport */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Transport</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Véhicule
              </label>
              <select
                name="camion"
                value={formData.camion}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={0}>Sélectionner un camion</option>
                {camions.map(camion => (
                  <option key={camion.id} value={camion.id}>
                    {camion.immatriculation} - {camion.marque} {camion.modele}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chauffeur
              </label>
              <select
                name="chauffeur"
                value={formData.chauffeur}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={0}>Sélectionner un chauffeur</option>
                {chauffeurs.map(chauffeur => (
                  <option key={chauffeur.id} value={chauffeur.id}>
                    {chauffeur.nom} {chauffeur.prenom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fournisseur
              </label>
              <select
                name="fournisseur"
                value={formData.fournisseur}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={0}>Sélectionner un fournisseur</option>
                {fournisseurs.map(fournisseur => (
                  <option key={fournisseur.id} value={fournisseur.id}>
                    {fournisseur.nom} - {fournisseur.cooperative}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pont Bascule
              </label>
              <select
                name="pont_bascule"
                value={formData.pont_bascule}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={0}>Sélectionner un pont bascule</option>
                {pontsBascule.map(pont => (
                  <option key={pont.id} value={pont.id}>
                    {pont.numero} - {pont.statut_display}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Section Observations */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Observations
          </label>
          <textarea
            name="observations"
            value={formData.observations}
            onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Observations sur la pesée..."
          />
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Création...
              </div>
            ) : (
              'Créer la Pesée'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WeighingForm;
